import React from "react";
import {
  ArrowRightIcon,
  BedIcon,
  BuildingIcon,
  CheckinIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  DefaultHotelIcon,
  TickCircleIcon,
  TimeIcon,
  WashIcon,
  WiFiIcon,
} from "../../../../helper/HwIcon";
import Slider from "react-slick";
import { calcNights, formatPrice, formatePriceFn } from "../../../../helper/commonFunction";
import { useSelector } from "react-redux";
import RoomDetailsPopup from "../../popups/RoomDetailsPopu‎p";
import { useLocation } from "react-router-dom";
import { useState } from "react";
import { X } from "lucide-react";

const NextArrow = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        position: "absolute",
        right: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "#EBEFFF",
        color: "#3264FF",
        border: "none",
        padding: "4px 4px",
        cursor: "pointer",
        borderRadius: "50%"
        // width:"25px",
        // height:"25px"
      }}
    >
      <ChevronRightIcon />
    </button>
  );
};

const PrevArrow = ({ onClick }) => {
  return (
    <button
      onClick={onClick}
      style={{
        position: "absolute",
        left: "10px",
        top: "50%",
        transform: "translateY(-50%)",
        zIndex: 2,
        background: "#EBEFFF",
        color: "#3264FF",
        border: "none",
        padding: "4px 4px",
        cursor: "pointer",
        borderRadius: "50%"
        // width:"25px",
        // height:"25px"
      }}
    >
      <ChevronLeftIcon />
    </button>
  );
};

const settings = {
  dots: false,
  arrows: true,
  infinite: false,
  speed: 500,
  slidesToShow: 1,
  slidesToScroll: 1,
  nextArrow: <NextArrow />,   // ✅ custom next
  prevArrow: <PrevArrow />,   // ✅ custom prev
  responsive: [
    {
      breakpoint: 768,
      settings: {
        slidesToShow: 1,
        slidesToScroll: 1,
      },
    },
  ],
};

const MEAL_FILTERS = [
  { key: "nomeal", label: "Room Only" },
  { key: "breakfast", label: "Breakfast" },
  { key: "half-board", label: "Half Board" },
  { key: "full-board", label: "Full Board" },
  { key: "all-inclusive", label: "All Inclusive" },
];

const boldDateInText = (text = "") =>
  String(text).replace(/([A-Z][a-z]{2}\s\d{1,2},\s\d{4})/g, '<strong style="font-weight:700">$1</strong>');

const BED_TYPES = ["King", "Queen", "Twin", "Double", "Single"];
const bedsFromCategory = (category = "") => {
  const c = String(category).toLowerCase();
  return BED_TYPES.filter((b) => new RegExp(`\\b${b.toLowerCase()}\\s+beds?\\b`).test(c));
};

const RoomsSkeleton = ({ count = 3 }) => (
  <>
    <style>{`@keyframes hwRoomPulse{0%,100%{opacity:1}50%{opacity:.45}}`}</style>
    {Array.from({ length: count }).map((_, i) => {
      const box = { background: "#e9edf3", borderRadius: 8, animation: "hwRoomPulse 1.4s ease-in-out infinite" };
      return (
        <div className="hw-hotel--singleListOut" key={i}>
          <div className="row">
            <div className="col-md-4">
              <div style={{ ...box, height: 170, marginBottom: 12 }} />
              <div style={{ ...box, height: 16, width: "70%", marginBottom: 8 }} />
              <div style={{ ...box, height: 14, width: "50%" }} />
            </div>
            <div className="col-md-8">
              <div style={{ ...box, height: 16, width: "40%", marginBottom: 12 }} />
              <div style={{ ...box, height: 14, width: "80%", marginBottom: 8 }} />
              <div style={{ ...box, height: 14, width: "60%", marginBottom: 18 }} />
              <div style={{ ...box, height: 40, width: 150 }} />
            </div>
          </div>
        </div>
      );
    })}
  </>
);

export const HotelOptions = ({ options, data, onSelectRoom, searchQuery, rooms, error, images }) => {
  const groupedOptions = options || {};
  const [selectedFilters, setSelectedFilters] = useState([]);
  const [visibleCount, setVisibleCount] = useState(3);
  const [isFiltering, setIsFiltering] = useState(false);

  const flashSkeleton = () => {
    setIsFiltering(true);
    setTimeout(() => setIsFiltering(false), 350);
  };

  const vendorParam = new URLSearchParams(useLocation().search).get("vendor");
  const isHotelRack = vendorParam === "HOTELRACK";

  const allRates = Object.values(groupedOptions).flat();
  const mealsPresent = new Set(allRates.map((r) => r?.mealValue).filter(Boolean));
  const hasFreeCancellation = allRates.some((r) => !!r?.deadlineDate);
  const bedsPresent = new Set();
  allRates.forEach((r) => bedsFromCategory(r?.rooomCategory).forEach((b) => bedsPresent.add(b)));

  const availableFilters = [];
  if (mealsPresent.size > 1) {
    MEAL_FILTERS.forEach((m) => {
      if (mealsPresent.has(m.key)) availableFilters.push({ key: `meal:${m.key}`, label: m.label });
    });
  }
  if (hasFreeCancellation) {
    availableFilters.push({ key: "free_cancellation", label: "Free Cancellation" });
  }
  if (isHotelRack && bedsPresent.size > 1) {
    BED_TYPES.forEach((b) => {
      if (bedsPresent.has(b)) availableFilters.push({ key: `bed:${b}`, label: `${b} Bed` });
    });
  }

  const matchesFilters = (rate) => {
    if (selectedFilters.length === 0) return true;
    const mealFilters = selectedFilters.filter((f) => f.startsWith("meal:")).map((f) => f.slice(5));
    const bedFilters = selectedFilters.filter((f) => f.startsWith("bed:")).map((f) => f.slice(4));
    const requireFreeCancellation = selectedFilters.includes("free_cancellation");

    if (mealFilters.length > 0 && !mealFilters.includes(rate?.mealValue)) return false;
    if (bedFilters.length > 0) {
      const beds = bedsFromCategory(rate?.rooomCategory);
      if (!bedFilters.some((b) => beds.includes(b))) return false;
    }
    if (requireFreeCancellation && !rate?.deadlineDate) return false;
    return true;
  };

  const toggleFilter = (key) => {
    flashSkeleton();
    setVisibleCount(3);
    setSelectedFilters((prev) =>
      prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]
    );
  };

  const clearFilters = () => {
    flashSkeleton();
    setSelectedFilters([]);
    setVisibleCount(3);
  };

  const filteredEntries = Object.entries(groupedOptions)
    .map(([label, rates]) => [label, (rates || []).filter(matchesFilters)])
    .filter(([, rates]) => rates.length > 0);

  const visibleEntries = filteredEntries.slice(0, visibleCount);
  const hasNoResults =
    !groupedOptions ||
    Object.keys(groupedOptions).length === 0 ||
    filteredEntries.length === 0;

  return (
    <div id="hotel_select_room" className="hw-hotel--singleListSection mb-20">
      <h2 className="h4 mb-20">Select Your Room</h2>
      {
        availableFilters.length > 0 && (
          <div className="hw-sortby--strip mb-20">
            {availableFilters.map((f) => (
              <button
                key={f.key}
                type="button"
                className={`hw-sortby--tab${selectedFilters.includes(f.key) ? " active" : ""}`}
                onClick={() => toggleFilter(f.key)}
              >
                {f.label}
              </button>
            ))}
            {selectedFilters.length > 0 && (
              <button type="button" className="hw-sortby--clear" onClick={clearFilters}>
                <X size={14} /> Clear
              </button>
            )}
          </div>
        )
      }
      {
        isFiltering ? (
          <RoomsSkeleton count={Math.min(visibleEntries.length || 3, 3)} />
        ) : hasNoResults ? (
          <div className="no-options-msg">No rooms available</div>
        ) : (
          <>
            {visibleEntries.map(([optionLabel, rates]) => (
              <HotelOption
                key={optionLabel}
                options={rates}
                optionLable={optionLabel}
                data={data}
                onSelectRoom={onSelectRoom}
                searchQuery={searchQuery}
                rooms={rooms}
                error={error}
                images={images}
              />
            ))}
            {filteredEntries.length > visibleCount && (
              <div className="text-center mt-16">
                <button
                  type="button"
                  onClick={() => setVisibleCount((c) => c + 3)}
                  style={{
                    padding: "10px 24px",
                    borderRadius: 6,
                    border: "1px solid #3264FF",
                    background: "#fff",
                    color: "#3264FF",
                    fontWeight: 600,
                    cursor: "pointer",
                  }}
                >
                  Load More
                </button>
              </div>
            )}
          </>
        )
      }
    </div>
  );
};

export const HotelOption = ({ options,optionLable, data, onSelectRoom, searchQuery, rooms, error, images, option }) => {
  const { agentActiveCurrency, displayPrice } = useSelector((state) => state.userData);
  let roomData = options?.[0]?.features
  let imgs = options?.[0]?.roomImages ?? images ?? [];
  const bed = options?.[0]?.guestPolicies?.find((policy) => policy.label === "Bed")?.description || "";
  const extras = options?.[0]?.roomExtras || {};
  const spotTax = options?.[0]?.spotTax;
  const infoChips = [];
  [extras?.bedConfig, extras?.beddingType].forEach((v) => {
    if (v && v.toLowerCase() !== (bed || "").toLowerCase() && !infoChips.some((c) => c.toLowerCase() === v.toLowerCase())) {
      infoChips.push(v);
    }
  });
  const vendorParam = new URLSearchParams(useLocation().search).get("vendor");
  const isHotelRack = (vendorParam) === "HOTELRACK";
  const [showDetail, setShowDetail] = useState(null)
  const singleOption = (options?.length || 0) === 1;
  return (
    <div className="hw-hotel--singleListOut">
      <div className="row">
        <div className="col-md-4 ">
          <div className="hw-singleListOut--image">
            {/* <div className="hw-hotel--listItemm"> */}
            {imgs?.length > 0 ? (
              <Slider {...settings}>
                {imgs.map((d, i) => (
                  <img key={i} width={100} height={250} style={{ borderRadius: '10px' }} src={d?.fastlyUrl || d} alt="image" />
                ))}
              </Slider>
            ) : (
              <div className="hw-singleListOut--imgPlaceholder">
                <DefaultHotelIcon />
              </div>
            )}
            {/* </div> */}
            <div className="hw-hotel--listItem__info">
              <div className="info-list">
                <div className="hw-text16 hw-fontBold hw-black mb-2">
                  {optionLable}
                </div>
                {
                  bed && <div className="d-flex d-flex-column flex-wrap">
                  {
                    bed && <div className="hw-text16 hw-fontBold d-flex align-items-center hw-gapxy10">
                    <BedIcon /> {bed}
                  </div>
                  }
                </div>
                }
              </div>
              {!singleOption && (
              <div className="info-list mb-2">
                <div className="d-flex flex-wrap hw-gapxy10">
                  {
                    (Array.isArray(roomData) && roomData.length > 0) &&
                      roomData.slice(0, 6).map((room) => {
                        return (
                          <div className="d-flex hw-gapy4 align-items-center hw-fontWeight--500 hw-icon--div hw-text14">
                            <TickCircleIcon />
                            {room?.name}
                          </div>
                        )
                      })
                  }
                </div>
              </div>
              )}
              <button
                    type="button"
                    className="hw-rateList__moreLink hw-link"
                    onClick={() => setShowDetail({...options?.[0],isCategory : true})}
                  >
                    More Details
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z" fill="#0062FF" />
                    </svg>
                  </button>
            </div>
          </div>
        </div>
        <div className="col-md-8 hw-singleListOut--content">
          {options?.map((option) => {
            return (
              <div className="hw-hotel--filterResult__listItemRightWrapper" style={singleOption ? { flexGrow: 0 } : undefined}>
                <div className="hw-hotel--filterResult__listItemInfo">
                  <div className="hw-hotel--filterResult__title hw-text16">
                    {option?.meal}
                  </div>
                  {option?.view ? (
                    <span
                      className="hw-rateView--pill"
                      style={{
                        display: "inline-flex",
                        alignItems: "center",
                        gap: "4px",
                        width: "fit-content",
                        background: "#EBEFFF",
                        color: "#3264FF",
                        fontWeight: 600,
                        fontSize: "1.2rem",
                        lineHeight: 1,
                        padding: "5px 10px",
                        borderRadius: "999px",
                        margin: "4px 0 2px",
                      }}
                    >
                      {option.view}
                    </span>
                  ) : null}
                  {
                    Array.isArray(option?.displayData) && option?.displayData?.length > 0 ? option?.displayData?.map((policy) => {
                      return (
                        <div className="hw-hotel--filterResult__location hw-fontWeight--500 hw-icon--div">
                          <span dangerouslySetInnerHTML={{ __html: boldDateInText(policy?.description) }} />
                        </div>
                      )
                    }) : null
                  }
                  <button
                    type="button"
                    className="hw-rateList__moreLink hw-link"
                    onClick={() => setShowDetail(option)}
                  >
                    View More Details
                    <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none">
                      <path d="M9.29289 18.7071C8.90237 18.3166 8.90237 17.6834 9.29289 17.2929L14.5858 12L9.29289 6.70711C8.90237 6.31658 8.90237 5.68342 9.29289 5.29289C9.68342 4.90237 10.3166 4.90237 10.7071 5.29289L16.7071 11.2929C17.0976 11.6834 17.0976 12.3166 16.7071 12.7071L10.7071 18.7071C10.3166 19.0976 9.68342 19.0976 9.29289 18.7071Z" fill="#0062FF" />
                    </svg>
                  </button>
                </div>
                <div className="hw-hotel--filterResult__listItemSelect">
                  <div className="mt-auto w-100">
                    <div class="h4 text-right">{formatePriceFn(agentActiveCurrency, formatPrice(option?.amount, true, true), displayPrice)}</div>
                    <div className="room text-right">
                      <span>{`${rooms} Room(s)`}</span>
                      <span>{calcNights(searchQuery?.checkinDate, searchQuery?.checkoutDate)} Night(s)</span>
                    </div>
                    {
                      (error === option?.rateKey?.[0] || error === option?.search_hash) && <span className="hw-p-error-cls text-right d-block">Room checkout failed.</span>
                    }
                    {/* <div className="text-right">{`Deadline Date : ${option?.deadlineDate}`}</div> */}
                    <div class="hw-mt--20">
                      <a
                        class=" hw-btn hw-btn--large hw-btn--primary w-100"
                        onClick={() => {
                          onSelectRoom(option);
                        }}
                      >
                        Book Now
                      </a>
                    </div>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>
      <RoomDetailsPopup
        show={!!showDetail}
        onClose={() => setShowDetail(null)}
        title={`${showDetail?.rooomCategory}`}
        features={showDetail?.features}
        guestPolicies={showDetail?.guestPolicies}
        isHotelRack={isHotelRack}
        meal={(showDetail?.room_name || "").split("|").slice(1).join("|").trim()}
        agentActiveCurrency={agentActiveCurrency}
        displayPrice={displayPrice}
        isCateory={showDetail?.isCategory}
        roomExtras={showDetail?.roomExtras}
        spotTax={showDetail?.spotTax}
        inclusions={showDetail?.inclusions}
      />
    </div>
  );
};